import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Tooltip,
} from "@mui/material";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import { useDispatch, useSelector } from "react-redux";
import CustomTable from "src/components/Table/Table";
import CustomBackdrop from "src/components/CustomBackdrop/CustomBackdrop";

import {
  getSchoolsListAction,
  addSchoolAction,
  deleteSchoolAction,
  updateSchoolStatusAction,
} from "src/api/schools";

export default function Schools() {
  const dispatch = useDispatch();
  const { schoolsList } = useSelector((state: any) => state.schools);

  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteItem, setDeleteItem] = useState<any>(null);

  const [form, setForm] = useState({
    name: "",
    address: "",
    pincode: "",
    landmark: "",
    phone: "",
    email: "",
    latitude: "",
    longitude: "",
  });

  const fetchData = async () => {
    setLoading(true);
    await dispatch(getSchoolsListAction({ pageNo: 1, pageSize: 10 }));
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleStatusChange = async (row: any, value: number) => {
    await dispatch(updateSchoolStatusAction({ id: row.id, status: value }));
    fetchData();
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    await dispatch(deleteSchoolAction(deleteItem.id));
    setDeleteItem(null);
    fetchData();
  };

  const handleSubmit = async () => {
    await dispatch(addSchoolAction(form));
    setOpenDialog(false);
    fetchData();
  };

  const columns = [
    { field: "name", headerName: "School Name", minWidth: 220 },
    { field: "address", headerName: "Address", minWidth: 300 },
    { field: "pincode", headerName: "Pincode", minWidth: 120 },
    { field: "landmark", headerName: "Landmark", minWidth: 260 },
    { field: "phone", headerName: "Phone", minWidth: 150 },
    { field: "email", headerName: "Email", minWidth: 250 },
    { field: "createdOn", headerName: "Created On", minWidth: 150 },
    { field: "location", headerName: "Location", minWidth: 180 },
    { field: "status", headerName: "Status", minWidth: 140 },
    { field: "action", headerName: "Action", minWidth: 100 },
  ];

  const actionContent = (column: any, row: any) => {
    // ✅ STATUS FIX (NO DOTS EVER)
    if (column === "status") {
      return (
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={row.status}
            onChange={(e: any) =>
              handleStatusChange(row, Number(e.target.value))
            }
            renderValue={(selected) => (
              <Box
                sx={{
                  fontWeight: 600,
                  color: selected ? "#117C39" : "#BA2222",
                  whiteSpace: "nowrap",
                }}
              >
                {selected ? "Active" : "Inactive"}
              </Box>
            )}
            sx={{
              backgroundColor: row.status ? "#dcfce7" : "#fee2e2",
              borderRadius: "6px",
              ".MuiSelect-select": {
                whiteSpace: "nowrap !important",
                overflow: "visible !important",
                textOverflow: "unset !important",
                paddingRight: "30px !important",
              },
            }}
            MenuProps={{ disablePortal: true }}
          >
            <MenuItem value={1}>Active</MenuItem>
            <MenuItem value={0}>Inactive</MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (column === "action") {
      return (
        <IconButton onClick={() => setDeleteItem(row)}>
          <DeleteOutlineIcon />
        </IconButton>
      );
    }

    if (column === "location") {
      const url = `https://www.google.com/maps?q=${row.latitude},${row.longitude}`;
      return (
        <Button
          size="small"
          startIcon={<LocationOnIcon />}
          href={url}
          target="_blank"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            color: "#2563eb",
            whiteSpace: "nowrap",
          }}
        >
          Show Location
        </Button>
      );
    }

    // ✅ ADDRESS (only truncated)
    if (column === "address") {
      return (
        <Tooltip title={row.address || ""}>
          <Typography
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 260,
            }}
          >
            {row.address}
          </Typography>
        </Tooltip>
      );
    }

    // ✅ LANDMARK (FULL TEXT NO CUT)
    if (column === "landmark") {
      return (
        <Typography
          sx={{
            whiteSpace: "nowrap",
            overflow: "visible",
            textOverflow: "unset",
          }}
        >
          {row.landmark}
        </Typography>
      );
    }

    // ✅ DEFAULT CELLS (NO ELLIPSIS)
    return (
      <Typography
        sx={{
          whiteSpace: "nowrap",
          overflow: "visible",
          textOverflow: "unset",
        }}
      >
        {row[column]}
      </Typography>
    );
  };

  return (
    <>
      {loading && <CustomBackdrop open />}

      <Box>
        {/* HEADER */}
        <Box sx={{ mb: 2 }}>
            <Typography
              variant="h5"
              sx={{
                lineHeight: "32px",
                mb: 1, // tighter spacing
              }}
            >
              Schools
            </Typography>

            <Button
              variant="outlined"
              startIcon={<PersonAddAltIcon />}
              onClick={() => setOpenDialog(true)}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                mt: 0.5, // slight spacing, not floating
              }}
            >
              Add School
            </Button>
          </Box>

        {/* ✅ SCROLL FIX (BOOKINGS STYLE) */}
        <Box
            sx={{
              "& .MuiTableContainer-root": {
                overflowX: "auto",

                // ✅ SCROLLBAR FIX STARTS HERE
                scrollbarHeight: "6px",

                "&::-webkit-scrollbar": {
                  height: "6px", // 👈 thickness reduced
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f5f9",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#cbd5e1",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "#94a3b8",
                },
              },

              "& .MuiTable-root": {
                minWidth: "1500px",
              },

              "& .MuiTableCell-root": {
                overflow: "visible !important",
                textOverflow: "unset !important",
              },
            }}
          >
          <CustomTable
            rows={schoolsList?.data || []}
            columns={columns}
            total={schoolsList?.total || 0}
            fetchData={() => {}}
            resetPagination={() => {}}
            action={actionContent}
          />
        </Box>
      </Box>

      {/* ADD */}
      <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: "16px",
              p: 1,
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              fontWeight: 600,
              fontSize: "20px",
            }}
          >
            Add School
          </DialogTitle>

          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2.5} mt={2}>
              
              {/* INPUT FIELDS */}
              {[
                { label: "School Name", key: "name" },
                { label: "Address", key: "address" },
                { label: "Pincode", key: "pincode" },
                { label: "Landmark", key: "landmark" },
                { label: "Phone", key: "phone" },
                { label: "Email", key: "email" },
                { label: "Latitude", key: "latitude" },     // ✅ added
                { label: "Longitude", key: "longitude" },   // ✅ added
              ].map((field) => (
                <TextField
                  key={field.key}
                  label={field.label}
                  fullWidth
                  value={form[field.key] || ""}
                  onChange={(e) =>
                    setForm({ ...form, [field.key]: e.target.value })
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                    },
                  }}
                />
              ))}

              {/* ACTIONS */}
              <Box display="flex" justifyContent="space-between" mt={3}>
                <Button
                  onClick={() => setOpenDialog(false)}
                  sx={{
                    color: "#ef4444",
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 500,
                    px: 3,
                  }}
                >
                  Save
                </Button>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>

      {/* DELETE */}
      <Dialog open={!!deleteItem} onClose={() => setDeleteItem(null)}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            Delete <b>{deleteItem?.name}</b>?
          </Typography>

          <Box display="flex" justifyContent="flex-end">
            <Button onClick={() => setDeleteItem(null)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={confirmDelete}>
              Delete
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}